import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  DEFAULT_DOT_OPACITY,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_OPACITY,
  DEFAULT_POINT_SIZE
} from "constants/chart.constants";
import {
  AVERAGE_LABEL,
  DEFAULT_WIDTH,
  FORECAST_SUFFIX,
  HIGHLIGHTED_WIDTH,
  LINE_OF_BEST_FIT_LABEL,
  P10_LABEL,
  P50_LABEL,
  P90_LABEL,
  STATISTIC_SERIES_SET
} from "entities/charts/eva/translators/constants";
import {
  getFeatureOpacity,
  getFeatureThickness,
  getHighlightedLineOpacityValue,
  getHighlightedLineSizeValue
} from "entities/charts/eva/translators/utils";
import {
  selectChartLineOpacityFeature,
  selectChartLineSizeFeature
} from "store/features";
import { RootState } from "store/rootReducer";

import { useChartEntities } from "./useChartEntities";

/**
 * Utility function to calculate proportional point size from line thickness
 */
export function getProportionalPointSize(thickness: number): number {
  return thickness + (DEFAULT_POINT_SIZE - DEFAULT_LINE_THICKNESS);
}

/**
 * Default thickness offset for highlighted statistics series and line of best fit
 */
const DEFAULT_HIGHLIGHT_OFFSET = 2;

/**
 * Helper function to check if a series is a forecast version of a statistics series
 * @param label - The series label to check
 * @returns The base statistics label if it's a forecast statistics series, null otherwise
 */
function getForecastStatisticsBaseLabel(label: string): string | null {
  if (!label.includes(FORECAST_SUFFIX)) {
    return null;
  }

  const baseLabel = label.split(FORECAST_SUFFIX)[0];
  return STATISTIC_SERIES_SET.has(baseLabel) ? baseLabel : null;
}

/**
 * Style properties for chart series (thickness and opacity)
 */
export interface SeriesStyle {
  thickness: number;
  opacity: number;
}

/**
 * Hook that provides comprehensive series styling logic with Redux integration.
 * Handles styling for all series types: statistical, best fit, and regular series.
 * Automatically fetches feature configurations from chart entities.
 *
 * - Type well styles gets handled through updateOptionsWithTypeWellsSeries
 *
 * @param chartId - The chart ID to get Redux state for
 * @returns Function to get series style based on series label and current style values
 */
export function useSeriesStyle(chartId: string) {
  const {
    average,
    p10,
    p50,
    p90,
    bestFitToggle,
    useSeriesStyleOverride,
    highlightedLineSize,
    highlightedLineOpacity,
    pointOpacity,
    pointSize
  } = useChartEntities(chartId);

  const lineOpacityFeature = useSelector((state: RootState) =>
    selectChartLineOpacityFeature(state, chartId)
  );
  const lineSizeFeature = useSelector((state: RootState) =>
    selectChartLineSizeFeature(state, chartId)
  );

  const currentLineOpacity = lineOpacityFeature?.properties?.value ?? DEFAULT_OPACITY;
  const currentLineThickness = lineSizeFeature?.properties?.value ?? DEFAULT_WIDTH;

  const currentStyle: SeriesStyle = useMemo(
    () => ({
      thickness: currentLineThickness,
      opacity: currentLineOpacity
    }),
    [currentLineThickness, currentLineOpacity]
  );

  /**
   * Gets the appropriate style for a series based on its label and API style (e.g thickness from API, defined from color palette default, statistical features...)
   *
   * Logic hierarchy:
   * 1. If it's a statistical series (P10, P50, P90, Average) or forecast version or Line of Best Fit:
   *    - Use the specific feature settings if configured
   *    - Fall back to DEFAULT constants if feature is not configured
   * 2. If override toggle is disabled:
   *    - Use original style values
   * 3. If override toggle is enabled:
   *    - Use current custom styles (from Redux)
   * @param label - The series label to check
   * @param originalStyle - The style from API data (optional)
   * @returns Style object with thickness and opacity
   */
  const getSeriesStyle = useCallback(
    (
      label: string,
      originalStyle?: { thickness?: number; opacity?: number }
    ): SeriesStyle => {
      const isStatisticsSeries = STATISTIC_SERIES_SET.has(label);
      const isLineOfBestFit = label.includes(LINE_OF_BEST_FIT_LABEL);
      const forecastStatisticsBaseLabel = getForecastStatisticsBaseLabel(label);
      const isForecastStatisticsSeries = forecastStatisticsBaseLabel !== null;

      // Statistical series (including forecast versions) and best fit always use feature settings
      if (isStatisticsSeries || isForecastStatisticsSeries) {
        const baseLabel = isForecastStatisticsSeries
          ? forecastStatisticsBaseLabel
          : label;

        switch (baseLabel) {
          case AVERAGE_LABEL:
            return {
              thickness: getFeatureThickness(average),
              opacity: getFeatureOpacity(average)
            };
          case P10_LABEL:
            return {
              thickness: getFeatureThickness(p10),
              opacity: getFeatureOpacity(p10)
            };
          case P50_LABEL:
            return {
              thickness: getFeatureThickness(p50),
              opacity: getFeatureOpacity(p50)
            };
          case P90_LABEL:
            return {
              thickness: getFeatureThickness(p90),
              opacity: getFeatureOpacity(p90)
            };
          default:
            return {
              thickness: DEFAULT_WIDTH,
              opacity: DEFAULT_OPACITY
            };
        }
      } else if (isLineOfBestFit) {
        return {
          thickness: getFeatureThickness(bestFitToggle),
          opacity: getFeatureOpacity(bestFitToggle)
        };
      }

      // For regular series: check override toggle
      if (!useSeriesStyleOverride?.active) {
        // Use API values when toggle is disabled
        return {
          thickness: originalStyle?.thickness ?? DEFAULT_WIDTH,
          opacity: originalStyle?.opacity ?? DEFAULT_OPACITY
        };
      }

      // Use Redux values when toggle is enabled
      return currentStyle;
    },
    [useSeriesStyleOverride?.active, currentStyle, average, p10, p50, p90, bestFitToggle]
  );

  /**
   * Gets the appropriate highlighted style for a series based on its label.
   * Used for hover states, selected states, and synced well highlighting.
   *
   * @param label - The series label to check
   * @returns Style object with highlighted thickness and opacity
   */
  const getHighlightedSeriesStyle = useCallback(
    (label: string): SeriesStyle => {
      const isStatisticsSeries = STATISTIC_SERIES_SET.has(label);
      const isLineOfBestFit = label.includes(LINE_OF_BEST_FIT_LABEL);
      const forecastStatisticsBaseLabel = getForecastStatisticsBaseLabel(label);
      const isForecastStatisticsSeries = forecastStatisticsBaseLabel !== null;

      // Statistical series (including forecast versions) and best fit use enhanced highlighting
      if (isStatisticsSeries || isForecastStatisticsSeries || isLineOfBestFit) {
        const currentStyle = getSeriesStyle(label);

        // For forecast statistics series, add DEFAULT_HIGHLIGHT_OFFSET to thickness and use default opacity
        if (isForecastStatisticsSeries) {
          return {
            thickness: currentStyle.thickness + DEFAULT_HIGHLIGHT_OFFSET,
            opacity: DEFAULT_OPACITY
          };
        }

        // For regular statistics series and line of best fit, add DEFAULT_HIGHLIGHT_OFFSET to thickness and use default opacity
        if (isStatisticsSeries || isLineOfBestFit) {
          return {
            thickness: currentStyle.thickness + DEFAULT_HIGHLIGHT_OFFSET,
            opacity: DEFAULT_OPACITY
          };
        }
      }

      // For regular series: check override toggle for highlighting
      if (!useSeriesStyleOverride?.active) {
        // Use default highlighted values when toggle is disabled
        return {
          thickness: HIGHLIGHTED_WIDTH,
          opacity: DEFAULT_OPACITY
        };
      }

      // Use Redux highlighted values when toggle is enabled
      return {
        thickness: getHighlightedLineSizeValue(highlightedLineSize),
        opacity: getHighlightedLineOpacityValue(highlightedLineOpacity)
      };
    },
    [
      getSeriesStyle,
      useSeriesStyleOverride?.active,
      highlightedLineSize,
      highlightedLineOpacity
    ]
  );

  /**
   * Gets the appropriate point style for scatter charts based on toggle state.
   * Used for crossPlot and other scatter chart point styling.
   *
   * @returns Style object with point opacity and size
   */
  const getPointStyle = useCallback((): { opacity: number; size: number } => {
    // For scatter points: check override toggle
    if (!useSeriesStyleOverride?.active) {
      // Use default point values when toggle is disabled
      return {
        opacity: DEFAULT_DOT_OPACITY,
        size: DEFAULT_POINT_SIZE
      };
    }

    // Use Redux point values when toggle is enabled
    return {
      opacity: pointOpacity?.properties?.value ?? DEFAULT_DOT_OPACITY,
      size: pointSize?.properties?.value ?? DEFAULT_POINT_SIZE
    };
  }, [
    useSeriesStyleOverride?.active,
    pointOpacity?.properties?.value,
    pointSize?.properties?.value
  ]);

  /**
   * Gets the appropriate highlighted point style for scatter charts.
   * Used for crossPlot and other scatter chart highlighting.
   * Highlighted points should follow the same sizing logic as regular points.
   *
   * @returns Style object with highlighted point opacity and size
   */
  const getHighlightedPointStyle = useCallback((): { opacity: number; size: number } => {
    // For highlighted points: use the same logic as regular points
    if (!useSeriesStyleOverride?.active) {
      // Use default values when toggle is disabled
      return {
        opacity: DEFAULT_DOT_OPACITY,
        size: DEFAULT_POINT_SIZE
      };
    }

    // Use Redux values when toggle is enabled (same as regular points)
    return {
      opacity: pointOpacity?.properties?.value ?? DEFAULT_DOT_OPACITY,
      size: pointSize?.properties?.value ?? DEFAULT_POINT_SIZE
    };
  }, [
    useSeriesStyleOverride?.active,
    pointOpacity?.properties?.value,
    pointSize?.properties?.value
  ]);

  return {
    getSeriesStyle,
    getHighlightedSeriesStyle,
    getPointStyle,
    getHighlightedPointStyle,
    getProportionalPointSize,
    currentStyle
  };
}
